import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare, faGlobe } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { FeedSignal } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";

interface SourceChipProps {
	signal: FeedSignal;
}

/**
 * Compact "Source" chip of a signal one-liner: type icon + tooltip with the
 * source meta, linking out to the original. Nothing renders for sourceless
 * signals (`sourceType: "none"` / no URL) — mirrors the mockup's chip.
 */
export const SourceChip: FunctionComponent<SourceChipProps> = ({ signal }) => {
	const { t } = useTranslation();

	if (signal.sourceType === "none" || !signal.sourceUrl) {
		return null;
	}

	const isLinkedin = signal.sourceType === "linkedin_post";
	const meta = signal.title ?? signal.line ?? "";

	return (
		<Tooltip
			content={
				<span className="flex max-w-[250px] flex-col gap-0.5 text-left">
					<span className="text-xs">{meta}</span>
					<span className="break-all text-[11px] text-medium-blue">{signal.sourceUrl}</span>
				</span>
			}
			variant="dark"
		>
			<a
				className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border-light bg-white px-1.5 py-0.5 text-[11px] font-semibold text-text-tertiary hover:border-brand-light hover:text-brand-light"
				data-feed-noselect="true"
				data-testid={`feed-source-chip-${signal.signalId}`}
				href={signal.sourceUrl}
				rel="noreferrer"
				target="_blank"
				onClick={(event) => event.stopPropagation()}
			>
				<span>{t("feed.runCard.source.label")}</span>
				<Icon className="h-2.5 w-2.5" icon={isLinkedin ? faLinkedinIn : faGlobe} />
				<Icon className="h-2 w-2" icon={faArrowUpRightFromSquare} />
			</a>
		</Tooltip>
	);
};
